import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Leaf, Bell } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Seasons() {
  const [products, setProducts] = useState([]);
  const [alertForm, setAlertForm] = useState({ email: "", product_id: "" });
  const [alertDone, setAlertDone] = useState(false);
  const [alertSending, setAlertSending] = useState(false);
  const now = new Date().getMonth() + 1;

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const ripeNow = products.filter((p) => p.season_start <= now && now <= p.season_end);

  const subscribe = async (e) => {
    e.preventDefault();
    setAlertSending(true);
    try {
      await axios.post(`${API}/alerts`, alertForm);
      setAlertDone(true);
      toast.success("You're on the list", { description: "We'll whisper when it's ripe." });
    } catch {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setAlertSending(false);
    }
  };

  return (
    <main data-testid="seasons-page" className="pt-40 pb-32">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mb-16 max-w-2xl"
        >
          <p className="overline-tag mb-6">The Season Calendar</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-light text-zinc-100 leading-[1.05]">
            What's ripe <span className="italic text-saffron">when</span>
          </h1>
          <p className="mt-8 text-base leading-relaxed text-zinc-400">
            Ontario seasons are short and glorious. This is when each variety leaves our fields —
            pre-order early, because when a bed is picked out, it's picked out.
          </p>
        </motion.div>

        {ripeNow.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="ripe-now-section"
            className="mb-16 border border-saffron/40 bg-saffron/5 p-8"
          >
            <p className="overline-tag mb-5 flex items-center gap-2">
              <Leaf className="w-4 h-4" /> Ripe right now — {MONTHS[now - 1]}
            </p>
            <div className="flex flex-wrap gap-3">
              {ripeNow.map((p) => (
                <span
                  key={p.id}
                  data-testid={`ripe-now-${p.id}`}
                  className="rounded-full border border-saffron/50 px-5 py-2 text-xs uppercase tracking-[0.15em] text-saffron"
                >
                  {p.name}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        <div className="overflow-x-auto pb-4">
          <div className="min-w-[820px]">
            <div className="grid grid-cols-[200px_repeat(12,1fr)] gap-x-1 mb-3">
              <span />
              {MONTHS.map((m, i) => (
                <span
                  key={m}
                  className={`text-center text-[10px] uppercase tracking-[0.15em] ${
                    i + 1 === now ? "text-saffron font-bold" : "text-zinc-600"
                  }`}
                >
                  {m}
                </span>
              ))}
            </div>
            <div className="flex flex-col divide-y divide-line border-y border-line">
              {products.map((p, idx) => (
                <motion.div
                  key={p.id}
                  data-testid={`season-row-${p.id}`}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className="grid grid-cols-[200px_repeat(12,1fr)] gap-x-1 items-center py-5"
                >
                  <div className="pr-4">
                    <p className="font-display text-lg text-zinc-100 leading-tight">{p.name}</p>
                    <p className="text-xs text-zinc-600 mt-1">
                      {MONTHS[p.season_start - 1]} – {MONTHS[p.season_end - 1]} · ${p.price.toFixed(2)}/{p.unit}
                    </p>
                  </div>
                  <div className="col-span-12 grid grid-cols-12 gap-x-1 items-center h-8">
                    <div
                      className={`h-2 rounded-full ${
                        p.season_start <= now && now <= p.season_end ? "bg-saffron" : "bg-forest"
                      }`}
                      style={{ gridColumn: `${p.season_start} / ${p.season_end + 1}` }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-8 text-xs text-zinc-500">
              <span className="flex items-center gap-2">
                <span className="w-6 h-2 rounded-full bg-saffron inline-block" /> In season now
              </span>
              <span className="flex items-center gap-2">
                <span className="w-6 h-2 rounded-full bg-forest inline-block" /> Harvest window
              </span>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-20 border border-line bg-surface p-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center"
        >
          <div>
            <p className="overline-tag mb-4 flex items-center gap-2">
              <Bell className="w-4 h-4" /> Season Alerts
            </p>
            <p className="font-display text-2xl sm:text-3xl text-zinc-100 leading-snug">
              Get the whisper <span className="italic text-saffron">the moment it's ripe.</span>
            </p>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md">
              Pick a variety, leave your email, and we'll tap your shoulder the day it comes in.
              One note per season — no noise, ever.
            </p>
          </div>
          {alertDone ? (
            <div data-testid="alert-confirmation" className="flex flex-col items-start gap-3">
              <Leaf className="w-8 h-8 text-saffron" />
              <p className="font-display text-2xl text-zinc-100">You're on the list.</p>
              <p className="text-sm text-zinc-400">Watch your inbox when the season turns.</p>
            </div>
          ) : (
            <form data-testid="alert-form" onSubmit={subscribe} className="flex flex-col gap-4">
              <select
                data-testid="alert-product-select"
                required
                value={alertForm.product_id}
                onChange={(e) => setAlertForm({ ...alertForm, product_id: e.target.value })}
                className="bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 focus-ring w-full"
              >
                <option value="" disabled>Choose a variety…</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <input
                data-testid="alert-email-input"
                required
                type="email"
                placeholder="Email"
                value={alertForm.email}
                onChange={(e) => setAlertForm({ ...alertForm, email: e.target.value })}
                className="bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-ring w-full"
              />
              <button
                data-testid="alert-submit-button"
                disabled={alertSending}
                className="rounded-full bg-saffron px-8 py-3.5 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors disabled:opacity-50 focus-ring w-fit"
              >
                {alertSending ? "Planting…" : "Notify Me"}
              </button>
            </form>
          )}
        </motion.div>

        <div className="mt-20 flex flex-wrap items-center justify-between gap-6 border border-line bg-surface p-10">
          <p className="font-display text-2xl sm:text-3xl text-zinc-100 max-w-xl leading-snug">
            See something you'll miss? <span className="italic text-saffron">Pre-order reserves your share of the bed.</span>
          </p>
          <Link
            to="/shop"
            data-testid="seasons-shop-cta"
            className="group flex items-center gap-3 rounded-full bg-saffron px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors focus-ring"
          >
            Pre-order the Harvest
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </main>
  );
}
